// Smart RFQ Configuration v6.0 — AI-Powered Smart RFQ & Proposal System
// 6-Step Architecture: Service → Conditions → Scope → Location → Timeline → Notes

// ─── STATE ───────────────────────────────────────────────────────────────────

export interface RFQState {
  // Step 1: Service Type
  service: string;
  // Step 2: Project Conditions
  wellheadPressure: string; // PSI value
  oilRate: string; // BOPD
  gasRate: string; // MMSCFD
  temperature: string; // °C
  sandProduction: string; // Low / Medium / High
  h2sPresence: string; // Yes / No
  // Step 3: Scope
  environment: string; // Onshore / Offshore
  duration: string; // Short-term / Long-term
  serviceModel: string; // Rental / Full Service
  // Step 4: Location
  country: string;
  locationType: string; // Remote / City / Desert
  // Step 5: Timeline
  timeline: string; // Immediate / 1-2 weeks / Planned
  // Step 6: Additional Notes
  additionalNotes: string;
  // Contact info (final submission)
  fullName: string;
  companyName: string;
  email: string;
  phone: string;
}

export const initialRFQState: RFQState = {
  service: '',
  wellheadPressure: '',
  oilRate: '',
  gasRate: '',
  temperature: '',
  sandProduction: '',
  h2sPresence: '',
  environment: '',
  duration: '',
  serviceModel: '',
  country: '',
  locationType: '',
  timeline: '',
  additionalNotes: '',
  fullName: '',
  companyName: '',
  email: '',
  phone: '',
};

// ─── SERVICE TYPES (15 Categories) ──────────────────────────────────────────

export const SERVICE_TYPES = [
  { id: 'surface-well-testing', label: 'Surface Well Testing', icon: '🔬' },
  { id: 'pressure-control', label: 'Pressure Control Equipment', icon: '⚡' },
  { id: 'stimulation-nitrogen', label: 'Stimulation & Nitrogen', icon: '💨' },
  { id: 'pumping-hydrotesting', label: 'Pumping & Hydrotesting', icon: '⚙️' },
  { id: 'equipment-rental', label: 'Equipment Rental', icon: '🏗️' },
  { id: 'chemicals-supply', label: 'Chemicals Supply', icon: '🧪' },
  { id: 'fabrication-skid', label: 'Fabrication & Skid Assembly', icon: '🏭' },
  { id: 'logistics-transport', label: 'Logistics & Transportation', icon: '🚛' },
  { id: 'engineering-services', label: 'Engineering Services', icon: '📐' },
  { id: 'ai-systems', label: 'AI Systems & Automation', icon: '🤖' },
  { id: 'spare-parts', label: 'Spare Parts & Consumables', icon: '🔧' },
  { id: 'solid-control', label: 'Solid Control & Waste Management', icon: '♻️' },
  { id: 'downhole-fiber', label: 'Downhole Fiber Optics', icon: '📡' },
  { id: 'pumping-solutions', label: 'Pumping Solutions', icon: '🔄' },
  { id: 'flame-detection', label: 'Flame Detection Systems', icon: '🔥' },
];

// ─── PROJECT CONDITIONS OPTIONS ──────────────────────────────────────────────

export const SAND_PRODUCTION_OPTIONS = [
  { id: 'low', label: 'Low' },
  { id: 'medium', label: 'Medium' },
  { id: 'high', label: 'High' },
];

export const H2S_OPTIONS = [
  { id: 'yes', label: 'Yes' },
  { id: 'no', label: 'No' },
];

// ─── SCOPE OPTIONS ───────────────────────────────────────────────────────────

export const ENVIRONMENT_OPTIONS = [
  { id: 'onshore', label: 'Onshore' },
  { id: 'offshore', label: 'Offshore' },
];

export const DURATION_OPTIONS = [
  { id: 'short-term', label: 'Short-term' },
  { id: 'long-term', label: 'Long-term' },
];

export const SERVICE_MODEL_OPTIONS = [
  { id: 'rental', label: 'Rental' },
  { id: 'full-service', label: 'Full Service' },
];

// ─── LOCATION OPTIONS ────────────────────────────────────────────────────────

export const COUNTRY_OPTIONS = [
  { id: 'ksa', label: 'Saudi Arabia (KSA)' },
  { id: 'uae', label: 'UAE' },
  { id: 'egypt', label: 'Egypt' },
  { id: 'kuwait', label: 'Kuwait' },
  { id: 'oman', label: 'Oman' },
  { id: 'bahrain', label: 'Bahrain' },
  { id: 'qatar', label: 'Qatar' },
  { id: 'iraq', label: 'Iraq' },
  { id: 'other', label: 'Other' },
];

export const LOCATION_TYPE_OPTIONS = [
  { id: 'remote', label: 'Remote' },
  { id: 'desert', label: 'Desert' },
  { id: 'city', label: 'City' },
];

// ─── TIMELINE OPTIONS ────────────────────────────────────────────────────────

export const TIMELINE_OPTIONS = [
  { id: 'immediate', label: 'Immediate' },
  { id: '1-2-weeks', label: '1–2 Weeks' },
  { id: 'planned', label: 'Planned' },
];

// ─── STEP CONFIGURATION ──────────────────────────────────────────────────────

export const STEP_LABELS = [
  'Service Selection',
  'Project Conditions',
  'Scope',
  'Location',
  'Timeline',
  'Notes & Contact',
  'Review & Adjust',
  'Engineering Validation',
];

export const TOTAL_STEPS = 7; // 0-indexed (8 steps: 0,1,2,3,4,5,6,7)
